import { AppContextDataContainer } from './AppContextDataContainer';
import ContextDataFactory from './ContextDataFactory';
import { PushContextData } from './PushContextData';
import { PushTelemetryData } from './PushTelemetryData';
import { QualtricsContextData, SessionContextData, ThemeKeyMapItem } from '../common/InternalTypes';
import { Util } from '../common/Util';

export class ContextDataController {
	private _window: Window;

	constructor() {
		this._window = window;
		this._initContext();
		this._mockUi5();
	}

	get globalWindow() {
		return this._window;
	}

	public reset() {
		if (this._window.sap) {
			if (this._window.sap.qtx) {
				this._window.sap.qtx = undefined;
			}
			if (this._window.sap.pxdata) {
				this._window.sap.pxdata = undefined;
			}
		}
	}

	public setAppContext(appContextDataContainer: AppContextDataContainer) {
		const verifiedAppContextData = appContextDataContainer.toQualtricsContextData();
		this._applyProperties(verifiedAppContextData, this._window.sap.qtx.appcontext);
	}

	public setSessionContext(sessionContextData: SessionContextData) {
		this._window.sap.qtx.session = {};
		const context = sessionContextData as unknown;
		this._applyProperties(context as QualtricsContextData, this._window.sap.qtx.session);
	}

	public setDeviceContext() {
		const deviceContextData = ContextDataFactory.createDeviceContextData();
		this._window.sap.qtx.device.type = deviceContextData.deviceType;
		this._window.sap.qtx.device.orientation = deviceContextData.orientationType;
		this._window.sap.qtx.device.osName = deviceContextData.operatingSystemName;
		this._window.sap.qtx.device.browserName = deviceContextData.browserName;
	}

	public setPushContext(pushContextData: PushContextData) {
		this._window.sap.qtx.push = {};
		if (pushContextData) {
			this._window.sap.qtx.push.type = pushContextData.type;
			this._window.sap.qtx.push.areaId = pushContextData.areaId;
			this._window.sap.qtx.push.triggerName = pushContextData.triggerName;
			this._window.sap.qtx.push.shortText = pushContextData.shortText;
			this._window.sap.qtx.push.payload = pushContextData.payloadToString;
		}
	}

	public setPushTelemetryContext(pushTelemetryData: PushTelemetryData) {
		this._window.sap.qtx.metrics.push = {};
		if (pushTelemetryData) {
			this._window.sap.qtx.metrics.push.triggered = pushTelemetryData.pushEventTriggeredCount;
			this._window.sap.qtx.metrics.push.executed = pushTelemetryData.pushEventExecutedCount;
			this._window.sap.qtx.metrics.push.snoozed = pushTelemetryData.pushEventSnoozedCount;
			this._window.sap.qtx.metrics.push.timeSinceLastPush = pushTelemetryData.timeSinceLastPushEvent;
		}
	}

	public setThemingDataContext(values: ThemeKeyMapItem[]) {
		this._window.sap.qtx.theming = {};
		if (values && values.length > 0) {
			values.forEach((item) => {
				this._window.sap.qtx.theming[item.embeddedDataKey] = item.cssValue;
			});
		}
	}

	public resetThemingContextData() {
		this._window.sap.qtx.theming = undefined;
	}

	public resetVariableContextData() {
		this._window.sap.qtx.appcontext = {};
		this._window.sap.qtx.push = {};
		this._window.sap.qtx.metrics.push = {};
	}

	private _initContext() {
		Util.ensureGlobalContext('pxdata', 'settings');
		Util.ensureGlobalContext('qtx', 'appcontext');
		Util.ensureGlobalContext('qtx', 'session');
		Util.ensureGlobalContext('qtx', 'push');
		Util.ensureGlobalContext('qtx', 'device');
		const metrics = Util.ensureGlobalContext('qtx', 'metrics');
		if (!metrics.push) {
			metrics.push = {};
		}
	}

	private _mockUi5() {
		Util.ensureGlobalContext();
		if (!this._window.sap.ui) {
			this._window.sap.ui = {
				core: {
					theming: {
						Parameters: {
							get: () => {
								return '';
							}
						}
					}
				}
			};
		}
	}

	private _applyProperties(source: QualtricsContextData, surveyContext: Record<string, unknown>) {
		if (source) {
			const keys = Object.keys(source);
			keys.forEach((key) => {
				surveyContext[key] = source[key];
			});
		}
	}
}
